/**
 * A round placeholder where a client's picture will go once documents are
 * hosted (Arthur, 14 October): the client's initials, or a plain silhouette
 * when the name gives none. No photo and no file is ever stored or fetched.
 *
 * - Initials come from the display name only ("Last, First" gives FL).
 * - The colour is chosen from the client's id, so the same client is always the
 *   same colour and nothing about them is encoded in it. Every colour carries
 *   white text above 7:1 contrast. None is red, so it can never read as the
 *   Critical band.
 */
/** The eight avatar tokens in index.css (--color-avatar-1 to 8). */
const PALETTE_SIZE = 8;

function hash(text: string): number {
  // FNV-1a: small, stable, and the same in every browser.
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function initialsOf(displayName: string): string {
  const firstLetter = (word: string | undefined) => (word ?? '').match(/\p{L}/u)?.[0] ?? '';
  const [last, rest] = displayName.includes(',') ? displayName.split(',', 2) : [displayName, ''];
  const lastWords = (last ?? '').trim().split(/\s+/);
  const restWords = (rest ?? '').trim().split(/\s+/).filter(Boolean);
  const given = restWords.length ? firstLetter(restWords[0]) : '';
  const family = displayName.includes(',') ? firstLetter(lastWords[0]) : firstLetter(lastWords.at(-1));
  const lead = displayName.includes(',') ? given + family : firstLetter(lastWords[0]) + (lastWords.length > 1 ? family : '');
  return lead.toUpperCase();
}

export function avatarColour(clientId: string): string {
  return `var(--color-avatar-${(hash(clientId) % PALETTE_SIZE) + 1})`;
}

export function ClientAvatar({ clientId, displayName, size = 64 }: { clientId: string; displayName: string; size?: number }) {
  const initials = initialsOf(displayName);
  return (
    <div
      aria-hidden="true"
      className="flex shrink-0 items-center justify-center rounded-full font-semibold text-white select-none"
      style={{ width: size, height: size, backgroundColor: avatarColour(clientId), fontSize: size * 0.38 }}
    >
      {initials || (
        <svg viewBox="0 0 24 24" width={size * 0.55} height={size * 0.55} fill="currentColor">
          <circle cx="12" cy="8" r="4.2" />
          <path d="M3.5 21c0-4.4 3.8-7.5 8.5-7.5s8.5 3.1 8.5 7.5z" />
        </svg>
      )}
    </div>
  );
}
